import { useState } from "react";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Lock, ShieldCheck, Zap } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REVENUE_OPTIONS = [
  { value: "under_10k", label: "Under $10k" },
  { value: "10k_25k", label: "$10k – $25k" },
  { value: "25k_50k", label: "$25k – $50k" },
  { value: "50k_100k", label: "$50k – $100k" },
  { value: "100k_plus", label: "$100k+" },
];

const TIB_OPTIONS = [
  { value: "under_6m", label: "Under 6 months" },
  { value: "6_12m", label: "6 – 12 months" },
  { value: "1_2y", label: "1 – 2 years" },
  { value: "2y_plus", label: "2+ years" },
];

const CREDIT_OPTIONS = [
  { value: "720_plus", label: "720+" },
  { value: "680_719", label: "680 – 719" },
  { value: "640_679", label: "640 – 679" },
  { value: "600_639", label: "600 – 639" },
  { value: "550_599", label: "550 – 599" },
  { value: "under_550", label: "Under 550" },
  { value: "not_sure", label: "Not sure" },
];

const TYPE_OPTIONS = [
  { value: "restaurant", label: "Restaurant" },
  { value: "bar", label: "Bar" },
  { value: "cafe", label: "Café" },
  { value: "food_truck", label: "Food Truck" },
  { value: "catering", label: "Catering" },
  { value: "other_food", label: "Other Food Business" },
];

const USE_OPTIONS = [
  { value: "inventory", label: "Inventory" },
  { value: "equipment", label: "Equipment" },
  { value: "expansion", label: "Expansion" },
  { value: "payroll", label: "Payroll" },
  { value: "renovation", label: "Renovation" },
  { value: "working_capital", label: "Working Capital" },
  { value: "other", label: "Other" },
];

const FUNDING_AMOUNT_OPTIONS = [
  { value: "under_10k", label: "Under $10,000" },
  { value: "10k_25k", label: "$10,000 – $25,000" },
  { value: "25k_50k", label: "$25,000 – $50,000" },
  { value: "50k_100k", label: "$50,000 – $100,000" },
  { value: "100k_250k", label: "$100,000 – $250,000" },
  { value: "250k_plus", label: "$250,000+" },
  { value: "not_sure", label: "Not Sure Yet" },
];

const HOMEOWNER_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "prefer_not_say", label: "Prefer not to say" },
];

const inputCls =
  "h-12 rounded-lg border border-white/10 bg-black/50 text-white placeholder:text-zinc-500 focus-visible:border-[#D4AF37] focus-visible:ring-1 focus-visible:ring-[#D4AF37]";

const initialForm = {
  full_name: "",
  email: "",
  cell_phone: "",
  business_name: "",
  type_of_business: "",
  monthly_revenue: "",
  time_in_business: "",
  use_of_funds: "",
  requested_funding_amount: "",
  credit_score_range: "",
  consent: false,
  dba_name: "",
  business_phone: "",
  commercial_address: "",
  homeowner_status: "",
};

export const LeadForm = () => {
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};

    if (!form.full_name.trim()) e.full_name = "Required";

    if (!form.email.trim() || !/^\S+@\S+\.\S+$/.test(form.email)) {
      e.email = "Valid email required";
    }

    if (
      !form.cell_phone.trim() ||
      form.cell_phone.replace(/\D/g, "").length < 7
    ) {
      e.cell_phone = "Valid phone required";
    }

    if (!form.business_name.trim()) e.business_name = "Required";
    if (!form.type_of_business) e.type_of_business = "Select an option";
    if (!form.monthly_revenue) e.monthly_revenue = "Select an option";
    if (!form.time_in_business) e.time_in_business = "Select an option";
    if (!form.use_of_funds) e.use_of_funds = "Select an option";
    if (!form.requested_funding_amount) e.requested_funding_amount = "Select an option";
    if (!form.credit_score_range) e.credit_score_range = "Select an option";

    if (!form.consent) {
      e.consent =
        "You must agree to the Terms of Service, Privacy Policy, and contact consent.";
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();

    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }

    setSubmitting(true);

    try {
      const payload = {
        ...form,
        dba_name: form.dba_name?.trim() || null,
        business_phone: form.business_phone?.trim() || null,
        commercial_address: form.commercial_address?.trim() || null,
        homeowner_status: form.homeowner_status || null,
      };

      await axios.post(`${API}/leads`, payload);
      toast.success("Submitted! Redirecting…");
      navigate("/thank-you");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      data-testid="lead-form"
      noValidate
      className="rounded-2xl border border-white/10 bg-[#0F0F0F] p-6 shadow-2xl shadow-black/60 sm:p-10"
    >
      <div className="mb-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-zinc-400">
        <span className="inline-flex items-center gap-1.5">
          <Lock className="h-3.5 w-3.5 text-[#D4AF37]" />
          Secure
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Zap className="h-3.5 w-3.5 text-[#D4AF37]" />
          60-second form
        </span>
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck className="h-3.5 w-3.5 text-[#D4AF37]" />
          No credit impact
        </span>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <FieldText
          id="full_name"
          label="Full Name"
          required
          value={form.full_name}
          onChange={(v) => setField("full_name", v)}
          placeholder="Jane Doe"
          error={errors.full_name}
          testid="full-name"
        />

        <FieldText
          id="business_name"
          label="Business Name"
          required
          value={form.business_name}
          onChange={(v) => setField("business_name", v)}
          placeholder="Lopez Bistro"
          error={errors.business_name}
          testid="business-name"
        />

        <FieldText
          id="email"
          type="email"
          label="Email"
          required
          value={form.email}
          onChange={(v) => setField("email", v)}
          placeholder="you@business.com"
          error={errors.email}
          testid="email"
        />

        <FieldText
          id="cell_phone"
          type="tel"
          label="Cell Phone"
          required
          value={form.cell_phone}
          onChange={(v) => setField("cell_phone", v)}
          placeholder="(555) 555-1234"
          error={errors.cell_phone}
          testid="cell-phone"
        />

        <FieldSelect
          label="Type of Business"
          required
          value={form.type_of_business}
          onChange={(v) => setField("type_of_business", v)}
          options={TYPE_OPTIONS}
          placeholder="Select type"
          error={errors.type_of_business}
          testid="type"
        />

        <FieldSelect
          label="Monthly Revenue"
          required
          value={form.monthly_revenue}
          onChange={(v) => setField("monthly_revenue", v)}
          options={REVENUE_OPTIONS}
          placeholder="Select range"
          error={errors.monthly_revenue}
          testid="revenue"
        />

        <FieldSelect
          label="Time in Business"
          required
          value={form.time_in_business}
          onChange={(v) => setField("time_in_business", v)}
          options={TIB_OPTIONS}
          placeholder="Select duration"
          error={errors.time_in_business}
          testid="tib"
        />

        <FieldSelect
          label="Use of Funds"
          required
          value={form.use_of_funds}
          onChange={(v) => setField("use_of_funds", v)}
          options={USE_OPTIONS}
          placeholder="What's it for?"
          error={errors.use_of_funds}
          testid="use"
        />
        
        <FieldSelect
  label="Requested Funding Amount"
  required
  value={form.requested_funding_amount}
  onChange={(v) => setField("requested_funding_amount", v)}
  options={FUNDING_AMOUNT_OPTIONS}
  placeholder="Select amount"
  error={errors.requested_funding_amount}
  testid="funding-amount"
/>

         <div>
          <FieldSelect
            label="Credit Score Range"
            required
            value={form.credit_score_range}
            onChange={(v) => setField("credit_score_range", v)}
            options={CREDIT_OPTIONS}
            placeholder="Select range"
            error={errors.credit_score_range}
            testid="credit"
            helper="Estimate is fine"
          />
        </div>
      </div>

      <div className="mt-6 flex items-start gap-3 rounded-lg border border-white/10 bg-black/40 p-4">
        <Checkbox
          id="consent"
          data-testid="checkbox-consent"
          checked={form.consent}
          onCheckedChange={(v) => setField("consent", !!v)}
          className="mt-0.5 border-white/30 data-[state=checked]:border-[#D4AF37] data-[state=checked]:bg-[#D4AF37] data-[state=checked]:text-black"
        />

        <Label
          htmlFor="consent"
          className="cursor-pointer text-sm font-normal leading-relaxed text-zinc-300"
        >
          By checking this box, you agree to our{" "}
          <Link
            to="/terms"
            className="text-[#D4AF37] underline-offset-4 hover:underline"
          >
            Terms of Service
          </Link>{" "}
          and{" "}
          <Link
            to="/privacy"
            className="text-[#D4AF37] underline-offset-4 hover:underline"
          >
            Privacy Policy
          </Link>{" "}
          and consent to be contacted regarding restaurant funding options.
        </Label>
      </div>

      {errors.consent && (
        <p className="mt-1 text-xs text-red-400" data-testid="error-consent">
          {errors.consent}
        </p>
      )}

      <div className="mt-10 rounded-xl border border-white/5 bg-black/30 p-5 sm:p-6">
        <div className="flex items-baseline gap-2">
          <h3 className="text-base font-semibold text-white">
            Additional Details
          </h3>
          <span className="text-xs text-zinc-500">(Optional)</span>
        </div>

        <p className="mt-1 text-sm text-zinc-400">
          The more information you provide, the faster we can match you with
          the best funding options.
        </p>

        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
          <FieldText
            id="dba_name"
            label="DBA Name"
            value={form.dba_name}
            onChange={(v) => setField("dba_name", v)}
            placeholder="Doing Business As"
            testid="dba-name"
          />

          <FieldText
            id="business_phone"
            type="tel"
            label="Business Phone"
            value={form.business_phone}
            onChange={(v) => setField("business_phone", v)}
            placeholder="(555) 555-1234"
            testid="business-phone"
          />

          <div className="md:col-span-2">
            <FieldText
              id="commercial_address"
              label="Commercial Address"
              value={form.commercial_address}
              onChange={(v) => setField("commercial_address", v)}
              placeholder="123 Main St, City, ST"
              testid="address"
            />
          </div>

          <div className="md:col-span-2">
            <FieldSelect
              label="Are You a Homeowner?"
              value={form.homeowner_status}
              onChange={(v) => setField("homeowner_status", v)}
              options={HOMEOWNER_OPTIONS}
              placeholder="Select"
              testid="homeowner"
            />
          </div>
        </div>
      </div>

      <Button
        type="submit"
        data-testid="submit-lead-button"
        disabled={submitting || !form.consent}
        className="mt-8 h-14 w-full rounded-lg bg-[#D4AF37] text-base font-bold text-black hover:bg-[#F0C94F] hover:shadow-[0_0_28px_rgba(212,175,55,0.45)] transition-all duration-300 disabled:opacity-60"
      >
        {submitting ? "Submitting…" : "Check My Options →"}
      </Button>

      <p className="mt-4 text-center text-xs text-zinc-500">
        Free to apply &middot; No upfront fees &middot; No obligation
      </p>
    </form>
  );
};

function FieldText({
  id,
  label,
  type = "text",
  value,
  onChange,
  placeholder,
  required = false,
  error,
  testid,
}) {
  return (
    <div>
      <Label htmlFor={id} className="mb-2 block text-sm text-zinc-300">
        {label} {required && <span className="text-[#D4AF37]">*</span>}
      </Label>

      <Input
        id={id}
        type={type}
        data-testid={`input-${testid}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={inputCls}
      />

      {error && (
        <p className="mt-1 text-xs text-red-400" data-testid={`error-${testid}`}>
          {error}
        </p>
      )}
    </div>
  );
}

function FieldSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  required = false,
  error,
  helper,
  testid,
}) {
  return (
    <div>
      <Label className="mb-2 block text-sm text-zinc-300">
        {label} {required && <span className="text-[#D4AF37]">*</span>}
      </Label>

      <Select value={value} onValueChange={onChange}>
        <SelectTrigger
          data-testid={`select-${testid}`}
          className={inputCls + " text-left"}
        >
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>

        <SelectContent className="border-white/10 bg-[#0F0F0F] text-white">
          {options.map((o) => (
            <SelectItem
              key={o.value}
              value={o.value}
              data-testid={`${testid}-opt-${o.value}`}
            >
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {helper && <p className="mt-1 text-xs text-zinc-500">{helper}</p>}

      {error && (
        <p className="mt-1 text-xs text-red-400" data-testid={`error-${testid}`}>
          {error}
        </p>
      )}
    </div>
  );
}
